import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Eye,
  Loader2,
  MapPin,
  Award,
  RefreshCw,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: "text-yellow-400 bg-yellow-400/10",
  approved: "text-green-400 bg-green-400/10",
  rejected: "text-red-400 bg-red-400/10",
};

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
};

export const AlumniAdminPage = () => {
  const [alumni, setAlumni] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("applications");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [alumniRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/alumni/all`),
        axios.get(`${API}/alumni/bookings/all`),
      ]);
      setAlumni(alumniRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/alumni/${id}/status?status=${status}`);
      setAlumni((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status } : a))
      );
      toast.success(`Alumni ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const toggleFeatured = async (id, featured) => {
    try {
      await axios.patch(`${API}/alumni/${id}/featured?featured=${!featured}`);
      setAlumni((prev) =>
        prev.map((a) => (a.id === id ? { ...a, featured: !featured } : a))
      );
      toast.success(featured ? "Removed from featured" : "Marked as featured");
    } catch {
      toast.error("Failed to update");
    }
  };

  const filteredAlumni = filter === "all" ? alumni : alumni.filter((a) => a.status === filter);
  const pending = alumni.filter((a) => a.status === "pending").length;

  return (
    <div data-testid="alumni-admin-page" className="pt-20">
      <section className="py-12 md:py-16 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-white">Alumni Admin</h1>
              <p className="font-body text-sm text-[#A3A3A3]">Manage applications and referral bookings</p>
            </div>
            <button onClick={fetchData} className="flex items-center gap-2 border border-[#262626] px-4 py-2 text-sm text-[#A3A3A3] hover:border-[#C5A059] transition-all">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bento-card p-4 text-center">
              <span className="font-heading text-2xl text-white">{alumni.length}</span>
              <p className="font-body text-xs text-[#A3A3A3] uppercase tracking-wider">Total Alumni</p>
            </div>
            <div className="bento-card p-4 text-center">
              <span className="font-heading text-2xl text-yellow-400">{pending}</span>
              <p className="font-body text-xs text-[#A3A3A3] uppercase tracking-wider">Pending</p>
            </div>
            <div className="bento-card p-4 text-center">
              <span className="font-heading text-2xl text-green-400">{alumni.filter((a) => a.status === "approved").length}</span>
              <p className="font-body text-xs text-[#A3A3A3] uppercase tracking-wider">Approved</p>
            </div>
            <div className="bento-card p-4 text-center">
              <span className="font-heading text-2xl text-[#C5A059]">{bookings.length}</span>
              <p className="font-body text-xs text-[#A3A3A3] uppercase tracking-wider">Referral Bookings</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-6 border-b border-[#262626]">
            <button
              onClick={() => setTab("applications")}
              className={`pb-3 font-body text-sm tracking-wider uppercase transition-all ${
                tab === "applications" ? "text-[#C5A059] border-b-2 border-[#C5A059]" : "text-[#A3A3A3]"
              }`}
            >
              <Users size={14} className="inline mr-2" /> Applications {pending > 0 && `(${pending})`}
            </button>
            <button
              onClick={() => setTab("bookings")}
              className={`pb-3 font-body text-sm tracking-wider uppercase transition-all ${
                tab === "bookings" ? "text-[#C5A059] border-b-2 border-[#C5A059]" : "text-[#A3A3A3]"
              }`}
            >
              Referral Bookings ({bookings.length})
            </button>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
            </div>
          )}

          {/* Applications Tab */}
          {tab === "applications" && !loading && (
            <>
              <div className="flex gap-2 mb-6">
                {["all", "pending", "approved", "rejected"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 text-xs font-body uppercase tracking-wider border transition-all ${
                      filter === f ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-[#262626] text-[#A3A3A3]"
                    }`}
                  >
                    {f} ({f === "all" ? alumni.length : alumni.filter((a) => a.status === f).length})
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {filteredAlumni.map((a) => {
                  const StatusIcon = statusIcons[a.status];
                  return (
                    <div key={a.id} className="bento-card p-5" data-testid={`admin-alumni-${a.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className="w-16 h-16 flex-shrink-0 overflow-hidden border border-[#262626]">
                            <img src={a.photo_url || "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&w=100"}
                              alt={a.name} className="w-full h-full object-cover" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading text-lg text-white">{a.name}</h3>
                              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${statusColors[a.status]}`}>
                                <StatusIcon size={10} className="inline mr-1" />{a.status}
                              </span>
                              {a.featured && (
                                <span className="text-[10px] bg-[#C5A059]/20 text-[#C5A059] px-2 py-0.5">
                                  <Star size={10} className="inline mr-1" />Featured
                                </span>
                              )}
                            </div>
                            {a.business_name && <p className="text-xs text-[#C5A059]">{a.business_name}</p>}
                            <div className="flex items-center gap-1 text-xs text-[#A3A3A3] mt-1">
                              <MapPin size={10} /> {a.location}, {a.region}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-[#A3A3A3] mt-1">
                              <Award size={10} className="text-[#C5A059]" /> {a.courses_completed?.join(", ")}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {a.services_offered?.map((s) => (
                                <span key={s} className="text-[10px] bg-[#C5A059]/10 text-[#C5A059] px-2 py-0.5">{s}</span>
                              ))}
                            </div>
                            <p className="text-xs text-[#A3A3A3] mt-1">Phone: {a.phone} | Bookings: {a.bookings_count || 0}</p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          {a.status !== "approved" && (
                            <button onClick={() => updateStatus(a.id, "approved")}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 text-green-400 text-xs hover:bg-green-500/20 transition-all">
                              <CheckCircle size={12} /> Approve
                            </button>
                          )}
                          {a.status !== "rejected" && (
                            <button onClick={() => updateStatus(a.id, "rejected")}
                              className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-all">
                              <XCircle size={12} /> Reject
                            </button>
                          )}
                          <button onClick={() => toggleFeatured(a.id, a.featured)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-[#C5A059]/10 text-[#C5A059] text-xs hover:bg-[#C5A059]/20 transition-all">
                            <Star size={12} /> {a.featured ? "Unfeature" : "Feature"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Bookings Tab */}
          {tab === "bookings" && !loading && (
            <div className="space-y-4">
              {bookings.length === 0 && (
                <p className="text-center text-[#A3A3A3] py-12">No referral bookings yet</p>
              )}
              {bookings.map((b) => (
                <div key={b.id} className="bento-card p-5" data-testid={`admin-booking-${b.id}`}>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="font-body text-xs text-[#C5A059] uppercase tracking-wider mb-1">Client</p>
                      <p className="text-white text-sm">{b.client_name}</p>
                      <p className="text-[#A3A3A3] text-xs">{b.client_phone}</p>
                      {b.client_email && <p className="text-[#A3A3A3] text-xs">{b.client_email}</p>}
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#C5A059] uppercase tracking-wider mb-1">Alumni</p>
                      <p className="text-white text-sm">{b.alumni_name}</p>
                      <p className="text-[#A3A3A3] text-xs">{b.date} at {b.time}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#C5A059] uppercase tracking-wider mb-1">Services</p>
                      <div className="flex flex-wrap gap-1">
                        {b.services?.map((s) => (
                          <span key={s} className="text-[10px] bg-[#C5A059]/10 text-[#C5A059] px-2 py-0.5">{s}</span>
                        ))}
                      </div>
                      {b.notes && <p className="text-[#A3A3A3] text-xs mt-1">Notes: {b.notes}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AlumniAdminPage;
